import React, { useEffect, useState } from "react";
import {
  KontaktdatenResponseType,
  KontaktdatenStatusModel,
  MitgliedschaftRequestType,
  StaatenkennungModel,
} from "../../../api";
import { KontaktdatenValidationModel, PropsViews } from "../model/PropsInterfaces";
import { emptyKontaktdaten, ExtendedKontaktdaten } from "../model/Kontaktdaten";
import { mitgliedschaftService } from "../service/MitgliedschaftService";
import { Hinweis } from "../view/Hinweise";

import IconFAZEmail from "../svgs/IconFAZEmail";
import IconFAZFax from "../svgs/IconFAZFax";
import IconFAZMobil from "../svgs/IconFAZMobil";
import IconFAZTel from "../svgs/IconFAZTel";
import IconFAZWeb from "../svgs/IconFAZWeb";
import IconArrowRight from "../svgs/IconArrowRight";
import IconButtonEdit from "../svgs/IconButtonEdit";
import {
  MitgliedschaftKontaktEditStateEnum,
  MitgliedschaftKontaktEditingAreaEnum,
} from "../enum/MitgliedschaftViewStatsEnums";
import { statistikAustriaService } from "../service/StatistikAustriaService";
import { unchangedMessage, successMessage, errorMessage, changeRequestSentText } from "../view/KontaktdatenMessages";
import { URL_BENUTZERKONTO, URL_FIRMEN_AZ, getLink } from "../constants/urls";
import { ServiceHotlineInfo } from "../view/InfoComponents";
import { getDescriptionNameByAuthenticatedRole } from "../service/UserService";
import KontaktdatenZusendeadresseView from "./KontaktdatenZusendeadresse";
import { convertLaenderBezeichnungToKuerzel, convertLaenderKuerzelToBezeichnung } from "../utils/LaenderUtils";
import {
  checkForError,
  checkIfValidationResultHasNoError,
  compareChanges,
  validateValues,
} from "../utils/KontaktdatenUtils";

export default function KontaktdatenView(props: PropsViews) {
  const [loading, setLoading] = useState(false);
  const [laenderdaten, setLaenderdaten] = useState<Array<StaatenkennungModel>>();
  const [manuelleEingabe, setManuelleEingabe] = useState<boolean>(false);
  const [editStateAddress, setEditStateAddress] = useState<MitgliedschaftKontaktEditStateEnum>(
    MitgliedschaftKontaktEditStateEnum.UNCHANGED,
  );
  const [editStateTel, setEditStateTel] = useState<MitgliedschaftKontaktEditStateEnum>(
    MitgliedschaftKontaktEditStateEnum.UNCHANGED,
  );
  const [editStateMobile, setEditStateMobile] = useState<MitgliedschaftKontaktEditStateEnum>(
    MitgliedschaftKontaktEditStateEnum.UNCHANGED,
  );
  const [editStateEmail, setEditStateEmail] = useState<MitgliedschaftKontaktEditStateEnum>(
    MitgliedschaftKontaktEditStateEnum.UNCHANGED,
  );
  const [kontaktdatenEnabled, setKontaktdatenEnabled] = useState<boolean>(false);
  const [kontaktdatenWKOBase, setKontaktdatenWKOBase] = useState<ExtendedKontaktdaten>();
  const [kontaktdatenChanged, setKontaktdatenChanged] = useState<ExtendedKontaktdaten>();
  const [kontaktdatenStatus, setKontaktdatenStatus] = useState<KontaktdatenStatusModel>({});
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [showUnchangedMessage, setShowUnchangedMessage] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<KontaktdatenValidationModel>();
  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    const requestParameterKontaktdatenFirmenAZ: MitgliedschaftRequestType = {};
    setLoading(true);

    mitgliedschaftService
      .isKontaktdatenEnabled()
      .then((enabled) => {
        if (enabled && enabled.data && enabled.data.data) {
          setKontaktdatenEnabled(true);
          mitgliedschaftService
            .getKontaktdaten(requestParameterKontaktdatenFirmenAZ)
            .then((kontaktdaten) => {
              let data: KontaktdatenResponseType = kontaktdaten.data;

              if (!laenderdaten) {
                statistikAustriaService
                  .getStatistikAustriaLaender("de")
                  .then((laenderdatenRes) => {
                    setLaenderdaten(laenderdatenRes.data);
                    loadData(data, laenderdatenRes.data);
                  })
                  .catch(() => {
                    setKontaktdatenEnabled(false);
                    setShowError(true);
                  });
              }
            })
            .catch(() => {
              setKontaktdatenEnabled(false);
              setShowError(true);
            });
        } else {
          setKontaktdatenEnabled(false);
        }
      })
      .catch(() => {
        setKontaktdatenEnabled(false);
        setShowError(true);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = (data: KontaktdatenResponseType, laender: Array<StaatenkennungModel>) => {
    if (data) {
      let kontaktdaten: ExtendedKontaktdaten = data;
      kontaktdaten.kontaktdatenGPDB = data.kontaktdaten?.filter(
        (kontakt) => kontakt.art && kontakt.art.indexOf("GP") === 0,
      );
      kontaktdaten.kontaktdatenFAZ = data.kontaktdaten?.filter(
        (kontakt) => kontakt.art && kontakt.art.indexOf("GP") < 0,
      );

      kontaktdaten.tel = data.kontaktdaten?.find((kontakt) => kontakt.art && kontakt.art === "GPTEL")?.wert;
      kontaktdaten.email = data.kontaktdaten?.find((kontakt) => kontakt.art && kontakt.art === "GPEM")?.wert;
      kontaktdaten.mobil = data.kontaktdaten?.find((kontakt) => kontakt.art && kontakt.art === "GPHAN")?.wert;
      kontaktdaten.vorname = data.vorname;
      kontaktdaten.nachname = data.zuname;
      kontaktdaten.strasse = data.zusendeadresse?.strassenName;
      kontaktdaten.hausnummer = data.zusendeadresse?.hausnummerVon;
      kontaktdaten.zusatz = data.zusendeadresse?.hausnummerZusatz;
      kontaktdaten.postfach = data.zusendeadresse?.postfach;
      kontaktdaten.plz = data.zusendeadresse?.plz;
      kontaktdaten.ort = data.zusendeadresse?.ortName;
      kontaktdaten.staatBezeichnung = data.zusendeadresse?.staatName;
      kontaktdaten.staat = convertLaenderBezeichnungToKuerzel(kontaktdaten.staatBezeichnung, laender);
      kontaktdaten.staatBezeichnung = convertLaenderKuerzelToBezeichnung(kontaktdaten.staat, laender);

      normalizeStrassenDaten(kontaktdaten);
      setKontaktdatenWKOBase(kontaktdaten);
      let changedKontaktdaten: ExtendedKontaktdaten = { ...kontaktdaten, ...emptyKontaktdaten };
      setKontaktdatenChanged(changedKontaktdaten);

      mitgliedschaftService
        .getKontaktdatenChangeRequestStatus({})
        .then((response) => {
          if (response.data.zusendeadresseGeaendert && response.data.zusendeadresseNeu) {
            setEditStateAddress(MitgliedschaftKontaktEditStateEnum.CHANGE_REQUESTED);
            mergeStatusData(response.data, kontaktdaten);
          }
          if (response.data.festnetzGeaendert) {
            setEditStateTel(MitgliedschaftKontaktEditStateEnum.CHANGE_REQUESTED);
          }
          if (response.data.mobilGeaendert) {
            setEditStateMobile(MitgliedschaftKontaktEditStateEnum.CHANGE_REQUESTED);
          }
          if (response.data.emailGeaendert) {
            setEditStateEmail(MitgliedschaftKontaktEditStateEnum.CHANGE_REQUESTED);
          }
          setKontaktdatenStatus(response.data);
        })
        .catch(() => {
          setShowError(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const mergeStatusData = (data: KontaktdatenStatusModel, kontaktdaten: ExtendedKontaktdaten | undefined) => {
    if (kontaktdaten && data.zusendeadresseGeaendert && data.zusendeadresseNeu) {
      if (!data.zusendeadresseNeu.ort) {
        data.zusendeadresseNeu.ort = kontaktdaten.ort;
      }
      if (!data.zusendeadresseNeu.plz) {
        data.zusendeadresseNeu.plz = kontaktdaten.plz;
      }
      if (!data.zusendeadresseNeu.landIso) {
        data.zusendeadresseNeu.landIso = kontaktdaten.staat;
      }
    }
  };

  const changeBearbeitungsstatus = (
    area: MitgliedschaftKontaktEditingAreaEnum,
    targetState: MitgliedschaftKontaktEditStateEnum,
  ) => {
    if (targetState === MitgliedschaftKontaktEditStateEnum.CHANGE_REQUESTED) {
      if (!processing) {
        setProcessing(true);
        let validValues = validateValues(area, validationError, setValidationError, kontaktdatenChanged, true);
        let kontaktdatenChangeRequestModel = compareChanges(
          area,
          kontaktdatenWKOBase,
          kontaktdatenChanged,
          manuelleEingabe,
        );
        if (validValues) {
          if (kontaktdatenChangeRequestModel) {
            setShowUnchangedMessage(false);
            mitgliedschaftService
              .createKontaktdatenChangeRequest(kontaktdatenChangeRequestModel)
              .then((response) => {
                let kontaktdatenStatusNew: KontaktdatenStatusModel = { ...kontaktdatenStatus };

                if (area === MitgliedschaftKontaktEditingAreaEnum.ADDRESS) {
                  setEditStateAddress(targetState);
                  kontaktdatenStatusNew.zusendeadresseGeaendert = true;
                  kontaktdatenStatusNew.zusendeadresseNeu = kontaktdatenChangeRequestModel?.zusendeadresseNeu;
                  mergeStatusData(kontaktdatenStatusNew, kontaktdatenWKOBase);
                } else if (area === MitgliedschaftKontaktEditingAreaEnum.PHONE) {
                  setEditStateTel(targetState);
                  kontaktdatenStatusNew.festnetzGeaendert = true;
                  kontaktdatenStatusNew.festnetzNeu = kontaktdatenChangeRequestModel?.festnetzNeu;
                } else if (area === MitgliedschaftKontaktEditingAreaEnum.MOBILE) {
                  setEditStateMobile(targetState);
                  kontaktdatenStatusNew.mobilGeaendert = true;
                  kontaktdatenStatusNew.mobilNeu = kontaktdatenChangeRequestModel?.mobilNeu;
                } else if (area === MitgliedschaftKontaktEditingAreaEnum.EMAIL) {
                  setEditStateEmail(targetState);
                  kontaktdatenStatusNew.emailGeaendert = true;
                  kontaktdatenStatusNew.emailNeu = kontaktdatenChangeRequestModel?.emailNeu;
                }

                setShowSuccess(true);
                setKontaktdatenStatus(kontaktdatenStatusNew);
              })
              .catch(() => {
                setShowError(true);
              })
              .finally(() => {
                setProcessing(false);
              });
          } else {
            setShowUnchangedMessage(true);
            setProcessing(false);
          }
        } else {
          setProcessing(false);
        }
      }
    } else {
      setShowUnchangedMessage(false);
      setValidationError(undefined);
      let changedKontaktdaten: ExtendedKontaktdaten = { ...kontaktdatenChanged, ...emptyKontaktdaten };
      setKontaktdatenChanged(changedKontaktdaten);

      if (changedKontaktdaten.staat !== "AT") {
        setManuelleEingabe(true);
      } else {
        setManuelleEingabe(false);
      }

      if (area === MitgliedschaftKontaktEditingAreaEnum.ADDRESS) {
        setEditStateAddress(targetState);
      } else if (area === MitgliedschaftKontaktEditingAreaEnum.PHONE) {
        setEditStateTel(targetState);
      } else if (area === MitgliedschaftKontaktEditingAreaEnum.MOBILE) {
        setEditStateMobile(targetState);
      } else if (area === MitgliedschaftKontaktEditingAreaEnum.EMAIL) {
        setEditStateEmail(targetState);
      }
    }
  };

  const css = `
    body:not(.doc).eService a:hover {
      color: #3D3F8C;
    }
  `;

  const headerLineWithEditButton = (title: string, area: MitgliedschaftKontaktEditingAreaEnum) => {
    return (
      <div
        className="kontaktdaten-header-with-edit-button"
        style={{
          border: "1px solid #dee2e6",
          borderWidth: "0 0 1px 0",
          display: "flex",
          alignItems: "flex-end",
          paddingBottom: "0.6rem",
          marginBottom: "1rem",
        }}
      >
        <div className="kontaktdaten-header-with-edit-button-title" style={{ flex: "1 1 200px" }}>
          <strong>{title}</strong>
        </div>
        {editStateAddress !== MitgliedschaftKontaktEditStateEnum.EDITING &&
          editStateTel !== MitgliedschaftKontaktEditStateEnum.EDITING &&
          editStateMobile !== MitgliedschaftKontaktEditStateEnum.EDITING &&
          editStateEmail !== MitgliedschaftKontaktEditStateEnum.EDITING &&
          ((area === MitgliedschaftKontaktEditingAreaEnum.ADDRESS &&
            editStateAddress === MitgliedschaftKontaktEditStateEnum.UNCHANGED) ||
            (area === MitgliedschaftKontaktEditingAreaEnum.PHONE &&
              editStateTel === MitgliedschaftKontaktEditStateEnum.UNCHANGED) ||
            (area === MitgliedschaftKontaktEditingAreaEnum.MOBILE &&
              editStateMobile === MitgliedschaftKontaktEditStateEnum.UNCHANGED) ||
            (area === MitgliedschaftKontaktEditingAreaEnum.EMAIL &&
              editStateEmail === MitgliedschaftKontaktEditStateEnum.UNCHANGED)) && (
            <button
              className="btn btn-secondary kontaktdaten-header-with-edit-button-button"
              style={{ flex: "0 0 80px" }}
              onClick={() => changeBearbeitungsstatus(area, MitgliedschaftKontaktEditStateEnum.EDITING)}
            >
              <IconButtonEdit></IconButtonEdit>
            </button>
          )}
      </div>
    );
  };

  const buttonGroup = (area: MitgliedschaftKontaktEditingAreaEnum) => {
    return (
      <div className="action-button mb-2">
        <>
          <button
            className="btn btn-secondary"
            onClick={() => changeBearbeitungsstatus(area, MitgliedschaftKontaktEditStateEnum.UNCHANGED)}
          >
            Abbrechen
          </button>
          &nbsp;
        </>
        <button
          className="btn btn-primary"
          onClick={(e) => {
            e.preventDefault();
            changeBearbeitungsstatus(area, MitgliedschaftKontaktEditStateEnum.CHANGE_REQUESTED);
          }}
          disabled={!!validationError || !!processing}
        >
          Speichern
        </button>
      </div>
    );
  };

  const validateValuesInternal = (
    area: MitgliedschaftKontaktEditingAreaEnum,
    kontaktdatenChanged: ExtendedKontaktdaten | undefined,
  ): boolean => {
    return validateValues(area, validationError, setValidationError, kontaktdatenChanged, true);
  };

  const updateKontaktdatenChanged = (fieldName: keyof ExtendedKontaktdaten, value: string) => {
    let newKontaktdaten = { ...kontaktdatenChanged, [fieldName]: value };
    if (fieldName === "staat") {
      newKontaktdaten.staatBezeichnung = convertLaenderKuerzelToBezeichnung(value, laenderdaten);
      validateValues(
        MitgliedschaftKontaktEditingAreaEnum.ADDRESS,
        validationError,
        setValidationError,
        newKontaktdaten,
        true,
      );
    }

    if (fieldName === "tel_land") {
      if (kontaktdatenChanged?.tel_vorwahl?.trim() !== "" && kontaktdatenChanged?.tel_nummer?.trim() !== "") {
        newKontaktdaten.tel =
          value.trim() + " " + kontaktdatenChanged?.tel_vorwahl?.trim() + " " + kontaktdatenChanged?.tel_nummer?.trim();
      } else {
        newKontaktdaten.tel = "";
      }
    }

    if (fieldName === "tel_vorwahl") {
      if (value.trim() !== "" && kontaktdatenChanged?.tel_nummer?.trim() !== "") {
        newKontaktdaten.tel =
          kontaktdatenChanged?.tel_land?.trim() + " " + value.trim() + " " + kontaktdatenChanged?.tel_nummer?.trim();
      } else {
        newKontaktdaten.tel = "";
      }
    }

    if (fieldName === "tel_nummer") {
      if (kontaktdatenChanged?.tel_vorwahl?.trim() !== "" && value.trim() !== "") {
        newKontaktdaten.tel =
          kontaktdatenChanged?.tel_land?.trim() + " " + kontaktdatenChanged?.tel_vorwahl?.trim() + " " + value.trim();
      } else {
        newKontaktdaten.tel = "";
      }
    }

    if (fieldName === "mobil_land") {
      if (kontaktdatenChanged?.mobil_vorwahl?.trim() !== "" && kontaktdatenChanged?.mobil_nummer?.trim() !== "") {
        newKontaktdaten.mobil =
          value.trim() +
          " " +
          kontaktdatenChanged?.mobil_vorwahl?.trim() +
          " " +
          kontaktdatenChanged?.mobil_nummer?.trim();
      } else {
        newKontaktdaten.mobil = "";
      }
    }

    if (fieldName === "mobil_vorwahl") {
      if (value.trim() !== "" && kontaktdatenChanged?.mobil_nummer?.trim() !== "") {
        newKontaktdaten.mobil =
          kontaktdatenChanged?.mobil_land?.trim() +
          " " +
          value.trim() +
          " " +
          kontaktdatenChanged?.mobil_nummer?.trim();
      } else {
        newKontaktdaten.mobil = "";
      }
    }

    if (fieldName === "mobil_nummer") {
      if (kontaktdatenChanged?.mobil_vorwahl?.trim() !== "" && value.trim() !== "") {
        newKontaktdaten.mobil =
          kontaktdatenChanged?.mobil_land?.trim() +
          " " +
          kontaktdatenChanged?.mobil_vorwahl?.trim() +
          " " +
          value.trim();
      } else {
        newKontaktdaten.mobil = "";
      }
    }

    setKontaktdatenChanged(newKontaktdaten);
  };

  const inputField = (
    inputId: string,
    fieldName: keyof ExtendedKontaktdaten,
    label: string,
    cols: string,
    disabled?: boolean,
  ) => {
    return (
      <>
        <div className={"element-input " + cols + " mb-3 pe-2"}>
          <div className={"element-" + fieldName}>
            <div className="form-floating">
              <input
                type="text"
                className="form-control"
                id={inputId}
                value={kontaktdatenChanged?.[fieldName] as string}
                onChange={(e) => {
                  const validationResult = checkForError(
                    validationError,
                    fieldName,
                    e.currentTarget.value,
                    undefined,
                    kontaktdatenChanged,
                    true,
                  );
                  checkIfValidationResultHasNoError(validationResult)
                    ? setValidationError(undefined)
                    : setValidationError(validationResult);
                  updateKontaktdatenChanged(fieldName, e.currentTarget.value);
                }}
                disabled={disabled}
                title={label}
              />
              <label htmlFor={inputId}>{label}</label>
              {validationError && validationError?.[fieldName] && (
                <small className="p-error">{validationError?.[fieldName]}</small>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  const normalizeStrassenDaten = (kontaktdaten: ExtendedKontaktdaten) => {
    if (kontaktdaten.strasse && kontaktdaten.strasse.length > 0) {
      let strassenName = kontaktdaten.strasse;
      // Strassenname kann Zahlen enthalten, gefolgt von zumindest einem Abstand, gefolgt von einer Zahl,
      // die noch einen zusätzlichen Buchstaben enthalten kann, gefolgt von "/","," oder " ",
      // gefolgt von einem optionalen Leerzeichen und einer beliebigen Zusatz Zeichenfolge
      let strassenNameMatcher = strassenName.match(/^(.*?)[\s]+(\d+[a-zA-Z]?)(?:[/, ]\s?(.*))?$/i);
      if (strassenNameMatcher && strassenNameMatcher.length > 0) {
        kontaktdaten.strasse = strassenNameMatcher[1].trim();
        if (strassenNameMatcher.length > 2 && strassenNameMatcher[2] && strassenNameMatcher[2].trim().length > 0) {
          kontaktdaten.hausnummer = strassenNameMatcher[2].trim();
        }
        if (
          strassenNameMatcher.length > 3 &&
          strassenNameMatcher[3] &&
          strassenNameMatcher[3].trim().length > 0 &&
          !kontaktdaten.zusatz
        ) {
          kontaktdaten.zusatz = strassenNameMatcher[3].trim();
        }
      }

      if (kontaktdaten.strasse && kontaktdaten.plz && kontaktdaten.ort) {
        statistikAustriaService
          .getStatistikAustriaStrassen(kontaktdaten.strasse + " " + kontaktdaten.plz + " " + kontaktdaten.ort)
          .then((data) => {
            if (data.data.length === 1) {
              let kontaktdatenChanged = { ...kontaktdaten };
              kontaktdatenChanged.strasseSA = data.data[0]?.kennziffer;
              setKontaktdatenWKOBase(kontaktdatenChanged);
            }
          });
      }
    }
  };

  return (
    <>
      <style>{css}</style>
      <main data-gtm-block="main">
        <section className="members bg--white" data-gtm-section="eServices - Kontaktdaten">
          <div className="container col-lg-7">
            <div className="members-header mb-5">
              <div className="headline">
                <div className="headline-bc">
                  <h1>Kontaktdaten</h1>
                  <h3 className="subtitle">
                    In diesem Bereich können Sie die Kontaktdaten Ihres Unternehmens verwalten.
                  </h3>
                </div>
              </div>
            </div>
            <div className="content">
              {(kontaktdatenEnabled && (
                <>
                  <h4>Kommunikation mit der WKO</h4>
                  <p>
                    Bitte halten Sie Ihre Kontaktdaten stets aktuell – so können wir Ihnen das bestmögliche Service
                    bieten.
                  </p>
                  <div className="hinweis">
                    <Hinweis
                      title="Interne Angaben"
                      message="Diese Information wird nicht nach außen weitergegeben und dient rein zur Übermittlung von Informationen durch die WKO."
                    />
                  </div>

                  {!loading && !showError && (
                    <>
                      {headerLineWithEditButton("Zusendeadresse", MitgliedschaftKontaktEditingAreaEnum.ADDRESS)}
                      {editStateAddress === MitgliedschaftKontaktEditStateEnum.UNCHANGED && (
                        <>
                          <p>{getDescriptionNameByAuthenticatedRole(props.userInfo)}</p>
                          {kontaktdatenWKOBase?.strasse && kontaktdatenWKOBase?.hausnummer && (
                            <p>
                              {kontaktdatenWKOBase?.strasse}&nbsp;{kontaktdatenWKOBase?.hausnummer}
                              {kontaktdatenWKOBase?.zusatz && (
                                <>
                                  {((/^\d.*$/i.test(kontaktdatenWKOBase?.zusatz) ||
                                    /^[a-zA-ZüÜöÖäÄß]{2,}.*$/i.test(kontaktdatenWKOBase?.zusatz)) && (
                                    <>, {kontaktdatenWKOBase?.zusatz}</>
                                  )) || <>{kontaktdatenWKOBase?.zusatz}</>}
                                </>
                              )}
                            </p>
                          )}
                          {kontaktdatenWKOBase?.postfach && <p>Postfach: {kontaktdatenWKOBase?.postfach}</p>}
                          <p>
                            {kontaktdatenWKOBase?.plz}&nbsp;{kontaktdatenWKOBase?.ort}
                          </p>
                          <p>{kontaktdatenWKOBase?.staatBezeichnung}</p>
                        </>
                      )}
                      {editStateAddress === MitgliedschaftKontaktEditStateEnum.CHANGE_REQUESTED && (
                        <>
                          {kontaktdatenStatus?.zusendeadresseGeaendert && kontaktdatenStatus.zusendeadresseNeu && (
                            <>
                              <p>{getDescriptionNameByAuthenticatedRole(props.userInfo)}</p>
                              {kontaktdatenStatus.zusendeadresseNeu.strasse &&
                                kontaktdatenStatus.zusendeadresseNeu.hausnummer && (
                                  <p>
                                    {kontaktdatenStatus.zusendeadresseNeu.strasse}&nbsp;
                                    {kontaktdatenStatus.zusendeadresseNeu.hausnummer}
                                    {kontaktdatenStatus.zusendeadresseNeu.zusatz && (
                                      <>
                                        {((/^\d.*$/i.test(kontaktdatenStatus.zusendeadresseNeu.zusatz) ||
                                          /^[a-zA-ZüÜöÖäÄß]{2,}.*$/i.test(
                                            kontaktdatenStatus.zusendeadresseNeu.zusatz,
                                          )) && <>, {kontaktdatenStatus.zusendeadresseNeu.zusatz}</>) || (
                                          <>{kontaktdatenStatus.zusendeadresseNeu.zusatz}</>
                                        )}
                                      </>
                                    )}
                                  </p>
                                )}
                              {kontaktdatenStatus.zusendeadresseNeu.postfach && (
                                <p>Postfach: {kontaktdatenStatus.zusendeadresseNeu.postfach}</p>
                              )}
                              <p>
                                {kontaktdatenStatus.zusendeadresseNeu?.plz}&nbsp;
                                {kontaktdatenStatus.zusendeadresseNeu?.ort}
                              </p>
                              <p>
                                {convertLaenderKuerzelToBezeichnung(
                                  kontaktdatenStatus.zusendeadresseNeu.landIso,
                                  laenderdaten,
                                )}
                              </p>
                            </>
                          )}
                          {changeRequestSentText}
                        </>
                      )}
                      {editStateAddress === MitgliedschaftKontaktEditStateEnum.EDITING && (
                        <>
                          <KontaktdatenZusendeadresseView
                            laenderdaten={laenderdaten}
                            userInfo={props.userInfo}
                            inputField={inputField}
                            manuelleEingabe={manuelleEingabe}
                            setManuelleEingabe={setManuelleEingabe}
                            kontaktdatenChanged={kontaktdatenChanged}
                            setKontaktdatenChanged={setKontaktdatenChanged}
                            updateKontaktdatenChanged={updateKontaktdatenChanged}
                            setShowError={setShowError}
                            validateValues={validateValuesInternal}
                            buttonGroup={buttonGroup}
                            showUnchangedMessage={showUnchangedMessage}
                          />
                        </>
                      )}
                      <p>&nbsp;</p>

                      {headerLineWithEditButton("Telefon - Festnetz", MitgliedschaftKontaktEditingAreaEnum.PHONE)}
                      {editStateTel === MitgliedschaftKontaktEditStateEnum.UNCHANGED && (
                        <>
                          {!kontaktdatenWKOBase?.tel && (
                            <p>Derzeit ist keine Festnetz Telefonnummer bei dieser Mitgliedschaft hinterlegt.</p>
                          )}
                          {kontaktdatenWKOBase?.tel && (
                            <p>
                              <IconFAZTel />
                              <span className="ms-3">{kontaktdatenWKOBase?.tel}</span>
                            </p>
                          )}
                        </>
                      )}
                      {editStateTel === MitgliedschaftKontaktEditStateEnum.CHANGE_REQUESTED && (
                        <>
                          {(kontaktdatenStatus?.festnetzGeaendert && (
                            <>
                              {(kontaktdatenStatus.festnetzNeu && kontaktdatenStatus.festnetzNeu.nummer && (
                                <>
                                  {(kontaktdatenStatus.festnetzNeu.laenderCode &&
                                    kontaktdatenStatus.festnetzNeu.vorwahl && (
                                      <p>
                                        <IconFAZTel />
                                        <span className="ms-3">
                                          {kontaktdatenStatus.festnetzNeu.laenderCode}{" "}
                                          {kontaktdatenStatus.festnetzNeu.vorwahl}{" "}
                                          {kontaktdatenStatus.festnetzNeu.nummer}
                                        </span>
                                      </p>
                                    )) || (
                                    <>
                                      <p>
                                        <IconFAZTel />
                                        <span className="ms-3">{kontaktdatenStatus.festnetzNeu.nummer}</span>
                                      </p>
                                    </>
                                  )}
                                </>
                              )) || (
                                <p>
                                  <IconFAZTel />
                                  <span className="ms-3">Nummer wird gelöscht</span>
                                </p>
                              )}
                            </>
                          )) ||
                            (kontaktdatenWKOBase?.tel && (
                              <p>
                                <IconFAZTel />
                                <span className="ms-3">{kontaktdatenWKOBase.tel}</span>
                              </p>
                            ))}
                          {changeRequestSentText}
                        </>
                      )}
                      {editStateTel === MitgliedschaftKontaktEditStateEnum.EDITING && (
                        <>
                          <p>Lassen Sie das Feld Nummer leer, wenn Sie den Eintrag löschen wollen.</p>
                          <div className="input-group" style={{ display: "flex" }}>
                            {inputField("input_tel_land", "tel_land", "Land (inkl. +)", "col-2")}
                            {inputField("input_tel_vorwahl", "tel_vorwahl", "Vorwahl (ohne beginnende 0)", "col-4")}
                            {inputField("input_tel_nummer", "tel_nummer", "Nummer", "col-6")}
                          </div>
                          {buttonGroup(MitgliedschaftKontaktEditingAreaEnum.PHONE)}
                          {showUnchangedMessage && unchangedMessage}
                        </>
                      )}
                      <p>&nbsp;</p>

                      {headerLineWithEditButton("Telefon - Mobil", MitgliedschaftKontaktEditingAreaEnum.MOBILE)}
                      {editStateMobile === MitgliedschaftKontaktEditStateEnum.UNCHANGED && (
                        <>
                          {!kontaktdatenWKOBase?.mobil && (
                            <p>Derzeit ist keine mobile Telefonnummer bei dieser Mitgliedschaft hinterlegt.</p>
                          )}
                          {kontaktdatenWKOBase?.mobil && (
                            <p>
                              <IconFAZMobil />
                              <span className="ms-3">{kontaktdatenWKOBase?.mobil}</span>
                            </p>
                          )}
                        </>
                      )}
                      {editStateMobile === MitgliedschaftKontaktEditStateEnum.CHANGE_REQUESTED && (
                        <>
                          {(kontaktdatenStatus?.mobilGeaendert && (
                            <>
                              {(kontaktdatenStatus.mobilNeu && kontaktdatenStatus.mobilNeu.nummer && (
                                <>
                                  {(kontaktdatenStatus.mobilNeu.laenderCode &&
                                    kontaktdatenStatus.mobilNeu.vorwahl && (
                                      <p>
                                        <IconFAZTel />
                                        <span className="ms-3">
                                          {kontaktdatenStatus.mobilNeu.laenderCode}{" "}
                                          {kontaktdatenStatus.mobilNeu.vorwahl} {kontaktdatenStatus.mobilNeu.nummer}
                                        </span>
                                      </p>
                                    )) || (
                                    <>
                                      <p>
                                        <IconFAZTel />
                                        <span className="ms-3">{kontaktdatenStatus.mobilNeu.nummer}</span>
                                      </p>
                                    </>
                                  )}
                                </>
                              )) || (
                                <p>
                                  <IconFAZTel />
                                  <span className="ms-3">Nummer wird gelöscht</span>
                                </p>
                              )}
                            </>
                          )) ||
                            (kontaktdatenWKOBase?.mobil && (
                              <p>
                                <IconFAZMobil />
                                <span className="ms-3">{kontaktdatenWKOBase.mobil}</span>
                              </p>
                            ))}

                          {changeRequestSentText}
                        </>
                      )}
                      {editStateMobile === MitgliedschaftKontaktEditStateEnum.EDITING && (
                        <>
                          <p>Lassen Sie das Feld Nummer leer, wenn Sie den Eintrag löschen wollen.</p>
                          <div className="input-group" style={{ display: "flex" }}>
                            {inputField("input_mobil_land", "mobil_land", "Land (inkl. +)", "col-2")}
                            {inputField("input_mobil_vorwahl", "mobil_vorwahl", "Vorwahl (ohne beginnende 0)", "col-4")}
                            {inputField("input_mobil_nummer", "mobil_nummer", "Nummer", "col-6")}
                          </div>
                          {buttonGroup(MitgliedschaftKontaktEditingAreaEnum.MOBILE)}
                          {showUnchangedMessage && unchangedMessage}
                        </>
                      )}
                      <p>&nbsp;</p>

                      {headerLineWithEditButton(
                        "E-Mail Adresse für Zusendungen",
                        MitgliedschaftKontaktEditingAreaEnum.EMAIL,
                      )}
                      {editStateEmail === MitgliedschaftKontaktEditStateEnum.UNCHANGED && (
                        <>
                          {!kontaktdatenWKOBase?.email && <p>Derzeit ist keine E-Mail Adresse hinterlegt.</p>}
                          {kontaktdatenWKOBase?.email && (
                            <p>
                              <IconFAZEmail />
                              <span className="ms-3">{kontaktdatenWKOBase?.email}</span>
                            </p>
                          )}
                        </>
                      )}
                      {editStateEmail === MitgliedschaftKontaktEditStateEnum.CHANGE_REQUESTED && (
                        <>
                          {(kontaktdatenStatus?.emailGeaendert && (
                            <>
                              {(kontaktdatenStatus.emailNeu && kontaktdatenStatus.emailNeu?.emailAdresse && (
                                <p>
                                  <IconFAZEmail />
                                  <span className="ms-3">{kontaktdatenStatus.emailNeu?.emailAdresse}</span>
                                </p>
                              )) || (
                                <p>
                                  <IconFAZEmail />
                                  <span className="ms-3">Email wird gelöscht</span>
                                </p>
                              )}
                            </>
                          )) || <p>{kontaktdatenWKOBase?.email}</p>}
                          {changeRequestSentText}
                        </>
                      )}
                      {editStateEmail === MitgliedschaftKontaktEditStateEnum.EDITING && (
                        <>
                          <p>Lassen Sie das Feld leer, um die E-Mail aus Ihren Daten zu löschen.</p>
                          {inputField("input_email", "email", "E-Mail", "col-12")}
                          {buttonGroup(MitgliedschaftKontaktEditingAreaEnum.EMAIL)}
                          {showUnchangedMessage && unchangedMessage}
                        </>
                      )}
                    </>
                  )}
                  {showSuccess && successMessage}
                  {showError && errorMessage}

                  <p>&nbsp;</p>
                  <p>&nbsp;</p>
                </>
              )) || (
                <>
                  {false && (
                    <>
                      <h4>Kommunikation mit der WKO</h4>
                      <div className="hinweis">
                        <Hinweis
                          title="Service bald verfügbar"
                          message="Diese Service ist für ihre Landeskammer noch nicht freigegeben."
                        />
                      </div>
                      <p>&nbsp;</p>
                      <p>&nbsp;</p>
                    </>
                  )}
                </>
              )}

              <h4>Kontaktdaten im Firmen A-Z</h4>
              <div>
                <Hinweis
                  title="Firmen A-Z"
                  message="Die öffentlich verfügbaren Kontaktdaten im WKO Firmen A-Z helfen Unternehmen und potenziellen Kunden, Sie schnell und einfach zu erreichen."
                />
                {(kontaktdatenWKOBase?.kontaktdatenFAZ && kontaktdatenWKOBase.kontaktdatenFAZ.length > 0 && (
                  <>
                    <p>&nbsp;</p>
                    {kontaktdatenWKOBase.kontaktdatenFAZ?.map((kontakt, index) => {
                      return (
                        <p key={index}>
                          {kontakt.art === "EM" && <IconFAZEmail />}
                          {kontakt.art === "FAX" && <IconFAZFax />}
                          {kontakt.art === "HAN" && <IconFAZMobil />}
                          {kontakt.art === "TEL" && <IconFAZTel />}
                          {kontakt.art === "URL" && <IconFAZWeb />}
                          {kontakt.art && kontakt.art.indexOf("GP") < 0 && (
                            <>
                              &nbsp;<span className="ms-3">{kontakt.wert}</span>
                            </>
                          )}
                        </p>
                      );
                    })}
                  </>
                )) || (
                  <>
                    <p>&nbsp;</p>
                    <p>Zurzeit sind keine Kontaktdaten im Firmen A-Z hinterlegt.</p>
                  </>
                )}
              </div>
              <div>
                <p>&nbsp;</p>
                <p>
                  <a href={getLink(URL_FIRMEN_AZ)} target="_blank" rel="noreferrer">
                    <strong>Firmen A-Z Profil bearbeiten</strong> <IconArrowRight></IconArrowRight>
                  </a>
                </p>
              </div>
              <p>&nbsp;</p>
              <p>&nbsp;</p>

              <h4>E-Mail Adresse zur Anmeldung auf wko.at und weiteren WKO Services</h4>
              <p>Sie sind zur Zeit angemeldet als</p>
              <p>
                <IconFAZEmail />
                &nbsp;<span className="ms-3">{props.userInfo?.wkisEmail}</span>
              </p>
              <Hinweis
                title="Benutzerkonto"
                message="Um Ihre persönlichen Daten zu verwalten, Ihr Passwort zu ändern, sowie um ein neues Unternehmen zuzuordnen, wechseln Sie bitte zu Ihrem Benutzerkonto."
              />
              <div>
                <p>&nbsp;</p>
                <p>
                  <a href={getLink(URL_BENUTZERKONTO)} target="_blank" rel="noreferrer">
                    <strong>Zum Benutzerkonto</strong> <IconArrowRight></IconArrowRight>
                  </a>
                </p>
              </div>
            </div>
            <ServiceHotlineInfo hotlineKey="kontaktdaten" userInfo={props.userInfo} />
          </div>
        </section>
      </main>
    </>
  );
}
